import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { HandHeart, User, Check } from 'lucide-react';
import PasswordStrength from '@/components/PasswordStrength';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import { pickWelcomeBackground } from '@/lib/branding';
import zxcvbn from 'zxcvbn';

/**
 * Public registration — two paths:
 *   1. "member" — Brotherhood+ user (self)  → post-register lands in /onboarding → /assessment
 *   2. "supporter" — Brotherhood Support user (loved one) → post-register lands in /onboarding → /app/support-home
 *
 * URL params:
 *   ?role=member|supporter → skips the picker
 *   ?invite=<token>        → auto-selects supporter path (family add-on flow)
 *
 * Legal-acceptance checkbox is required on the form step.
 */
export default function Register() {
  const { register, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const inviteToken = params.get('invite') || null;
  const roleParam = params.get('role');
  let forcedRole = null;
  if (inviteToken) forcedRole = 'supporter';
  else if (roleParam === 'supporter') forcedRole = 'supporter';
  else if (roleParam === 'member') forcedRole = 'member';

  const [role, setRole] = useState(forcedRole || null);

  // Carry the billing interval chosen on the landing page into checkout.
  const planParam = params.get('plan');
  useEffect(() => {
    if (planParam === 'annual' || planParam === 'monthly') {
      try { localStorage.setItem('bp_preferred_interval', planParam); } catch { /* ignore */ }
    }
  }, [planParam]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bg] = useState(pickWelcomeBackground);

  const submit = async (e) => {
    e.preventDefault();
    if (!role) { toast.error('Please choose a path first.'); return; }
    if (!acceptedLegal) { toast.error('Please accept the Privacy Policy and Terms of Service to continue.'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    const strength = zxcvbn(password).score;
    if (strength < 2) { toast.error('That password is too easy to guess. Try a longer passphrase.'); return; }
    setLoading(true);
    try {
      await register(email.trim().toLowerCase(), password, name.trim(), { role, invite_token: inviteToken });
      toast.success('Account created — welcome to Brotherhood+.');
      // If they completed the pre-assessment "about you" step, save it to the new
      // account (age + race/ethnicity for tailoring), then show the membership paywall.
      let savedProfile = false;
      try {
        const raw = sessionStorage.getItem('bp_assessment_profile');
        if (role === 'member' && raw) {
          const prof = JSON.parse(raw);
          await http.post('/auth/onboarding', {
            birth_year: prof.birth_year || null,
            ethnicity: prof.ethnicity || null,
            goals: [],
            growth_topics: [],
          });
          sessionStorage.removeItem('bp_assessment_profile');
          savedProfile = true;
        }
      } catch { /* non-fatal — fall through to routing */ }
      // Refresh so the client knows onboarding is complete (AppShell won't bounce to /onboarding).
      if (savedProfile) { try { await refresh(); } catch { /* noop */ } }
      // Trial auto-starts at signup (backend sets subscription_status=trialing),
      // so new members go through onboarding → assessment → Home, never the paywall.
      nav('/onboarding');                // 18+ age gate + preferences, then Home on trial
    } catch (err) {
      const d = err?.response?.data?.detail;
      let msg = 'Registration failed.';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d.length && d[0]?.msg) msg = d[0].msg;
      toast.error(msg);
    } finally { setLoading(false); }
  };

  // ── Step 1: role picker (skipped when URL params or invite forces the role)
  if (!role) {
    return (
      <div className="min-h-screen bg-navy bg-cover bg-center relative flex items-center justify-center p-6" style={{ backgroundImage: `url(${bg})` }}>
        <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/45 to-navy/85" />
        <div className="relative z-10 w-full max-w-md" data-testid="register-role-picker">
          <Link to="/login" className="text-sm text-white/70 hover:text-white">← Back</Link>
          <div className="mt-6 bg-white rounded-3xl p-8 card-soft">
            <h1 className="font-heading text-3xl mb-2 text-navy">Who are you here for?</h1>
            <p className="text-navy/60 text-sm mb-6">Two paths. One brotherhood.</p>
            <div className="space-y-3">
              <button
                type="button"
                data-testid="register-role-member"
                onClick={() => setRole('member')}
                style={{ animationDelay: '80ms', opacity: 0 }}
                className="animate-fade-up w-full text-left bg-navy text-white rounded-2xl p-5 hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.985] transition-smooth"
              >
                <div className="flex items-start gap-3">
                  <User size={22} className="mt-0.5 text-gold shrink-0" strokeWidth={1.75} />
                  <div>
                    <p className="font-heading text-lg">I&apos;m here for myself</p>
                    <p className="text-white/70 text-sm mt-1">
                      Brotherhood+ — mental wellness, growth plans, an AI Coach, journal, and community.
                    </p>
                    <div className="mt-3 pt-3 border-t border-white/15" data-testid="role-trial-member">
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold">Your 7-day free trial includes</p>
                      <ul className="mt-1.5 space-y-1">
                        {['The AI Coach in your corner', 'Guided growth plans & daily check-ins', 'Private journal + the lounge'].map((t) => (
                          <li key={t} className="flex items-start gap-1.5 text-white/75 text-[13px]">
                            <Check size={13} className="text-gold mt-0.5 shrink-0" strokeWidth={2.5} />{t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </button>
              <button
                type="button"
                data-testid="register-role-supporter"
                onClick={() => setRole('supporter')}
                style={{ animationDelay: '180ms', opacity: 0 }}
                className="animate-fade-up w-full text-left bg-white border border-navy/10 rounded-2xl p-5 hover:bg-beige/50 hover:-translate-y-0.5 active:scale-[0.985] transition-smooth"
              >
                <div className="flex items-start gap-3">
                  <HandHeart size={22} className="mt-0.5 text-gold shrink-0" strokeWidth={1.75} />
                  <div>
                    <p className="font-heading text-lg text-navy">I&apos;m here to support someone I care about</p>
                    <p className="text-navy/60 text-sm mt-1">
                      Brotherhood Support — guides, an AI Support Coach, and crisis resources for loved ones of a man.
                    </p>
                    <div className="mt-3 pt-3 border-t border-navy/10" data-testid="role-trial-supporter">
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold">Your 7-day free trial includes</p>
                      <ul className="mt-1.5 space-y-1">
                        {['Your own AI Support Coach', 'The full situation-by-situation guide library', 'Warning-sign & crisis resources'].map((t) => (
                          <li key={t} className="flex items-start gap-1.5 text-navy/70 text-[13px]">
                            <Check size={13} className="text-gold mt-0.5 shrink-0" strokeWidth={2.5} />{t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </button>
            </div>
            <p className="text-sm text-navy/60 mt-6 text-center">
              Already have an account?{' '}
              <Link to="/login?mode=signin" data-testid="register-to-login" className="text-navy font-medium">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2: the actual form. Copy adapts to role.
  const isSupporter = role === 'supporter';
  return (
    <div className="min-h-screen bg-navy bg-cover bg-center relative flex items-center justify-center p-6" style={{ backgroundImage: `url(${bg})` }}>
      <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/45 to-navy/85" />
      <div className="relative z-10 w-full max-w-md" data-testid="register-form-wrap">
        {!forcedRole && (
          <button onClick={() => setRole(null)} className="text-sm text-white/70 hover:text-white" data-testid="register-role-back">
            ← Choose a different path
          </button>
        )}
        <div className="mt-6 bg-white rounded-3xl p-8 card-soft">
          <h1 className="font-heading text-3xl mb-2 text-navy">
            {isSupporter ? 'Join Brotherhood Support' : 'Join Brotherhood+'}
          </h1>
          <p className="text-navy/60 text-sm mb-6">
            {isSupporter
              ? 'Support the men in your life with confidence. All features are currently free.'
              : 'A private space for men to grow, at your pace. All features are currently free.'}
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Name</label>
              <input
                data-testid="register-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Email</label>
              <input
                data-testid="register-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Password</label>
              <input
                data-testid="register-password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold"
              />
              <PasswordStrength password={password} data-testid="register-password-strength" />
              <p className="mt-1 text-[11px] text-navy/45">Minimum 8 characters. Passphrases beat clever passwords.</p>
            </div>
            <label className="flex items-start gap-3 pt-1 cursor-pointer select-none" data-testid="register-legal-wrap">
              <input
                type="checkbox"
                data-testid="register-legal-checkbox"
                checked={acceptedLegal}
                onChange={(e) => setAcceptedLegal(e.target.checked)}
                className="mt-1 h-4 w-4 accent-navy shrink-0"
              />
              <span className="text-xs text-navy/70 leading-relaxed">
                I&apos;ve read and agree to the{' '}
                <Link to="/privacy" target="_blank" className="text-navy font-medium underline underline-offset-2">Privacy Policy</Link>
                {' '}and{' '}
                <Link to="/terms" target="_blank" className="text-navy font-medium underline underline-offset-2">Terms of Service</Link>.
              </span>
            </label>
            <button
              data-testid="register-submit"
              type="submit"
              disabled={loading || !acceptedLegal}
              className="w-full bg-navy text-white rounded-full py-4 font-medium hover:bg-navy-soft transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating…' : (isSupporter ? 'Create Brotherhood Support account' : 'Create account')}
            </button>
          </form>
          <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-widest text-navy/40">
            <span className="flex-1 h-px bg-navy/10" />
            <span>or</span>
            <span className="flex-1 h-px bg-navy/10" />
          </div>
          <GoogleSignInButton
            label={isSupporter ? 'Continue with Google as Supporter' : 'Continue with Google'}
            nextUrl={isSupporter ? '/app/support-home' : '/app'}
            role={role}
            testId="register-google-btn"
          />
          <p className="text-[11px] text-navy/45 mt-3 text-center leading-relaxed">
            By continuing with Google, you also agree to the{' '}
            <Link to="/privacy" target="_blank" className="underline underline-offset-2">Privacy Policy</Link>
            {' '}and{' '}
            <Link to="/terms" target="_blank" className="underline underline-offset-2">Terms of Service</Link>.
          </p>
          <p className="text-sm text-navy/60 mt-6 text-center">
            Already have an account?{' '}
            <Link to="/login?mode=signin" data-testid="register-to-login" className="text-navy font-medium">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
